// Auto Autism Patient Report Helper
// ---------------------------------
// Purpose:
// Keep the Adult Autism patient-facing report in sync with the clinician-approved
// section reports (History, SSI, Observation, MDT Review, Feedback Session). When
// a clinician finishes reviewing a section and ALL five section reports are
// approved, and the previously finalised patient report is older than those
// approvals (or was never finalised), we re-render the patient report.
//
// This mirrors the ADHD v2 flow in autoPatientReport.js (rerenderPatientReportIfStale)
// so both products behave identically: no manual "Generate" button, the report just
// keeps itself current as sections are approved and re-approved.
//
// Why a separate file (not a shared abstraction with the ADHD helper):
// The ADHD helper is welded to the ADHD data model — `dataModel.6_manageReports`,
// report_logic_version gating, determinePatientReportType, and TOC-QbCheck staleness.
// The Autism model is different: five reportContent subcollection docs hold the
// approval flags, and the patient report status/anchor live directly on the patient
// header. Forcing one shared function would make it conditional-heavy and leaky, so we
// keep two small parallel helpers and share only the genuinely reusable primitive
// (waitForProcessingComplete, via a selector).
//
// Design choices (kept in step with autoPatientReport.js):
// - Read-before-write with short retry: after a section exit, Firestore server
//   timestamps can lag for a moment. We read fresh with a brief retry to avoid racing
//   that lag.
// - Strict staleness check: only regenerate if the most recent section approval is
//   newer than the patient report's approved timestamp (the staleness anchor).
// - Wait-then-retry for concurrency: when another render is already in-flight (e.g. two
//   sections approved in quick succession, or an MDT approving together), we wait for it
//   to finish via an onSnapshot listener, then re-check staleness and trigger exactly one
//   retry if the completed render didn't capture the latest approval. This guarantees
//   correctness without infinite retry loops.
// - Fire-and-forget from UI: callers don't await this; the actual render runs in the CF.
//
// Loop prevention:
// TriggerRenderAutismPatientReport writes autismPatientReport.prReportApprovedAt at
// TRIGGER time (before the CF runs), which advances past every section's approval
// timestamp. The render CF deliberately never rewrites prReportApprovedAt on completion,
// so the post-render recheck correctly sees "not stale" and stops.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  getPatientHeaderPathSegments,
  getPatientReportContentPathSegments,
} from '../constants/adultAutismFirestorePaths';
import { AUTISM_STAGE_KEYS, STAGE_TO_REPORT_PREFIX, getEnabledAutismStages } from '../constants/adultAutismStages';
import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';
import { TriggerRenderAutismPatientReport } from './TriggerRenderAutismPatientReport';
import { waitForProcessingComplete } from './processingStatusHelpers';

// Derive the stages that gate patient-report finalisation from the same
// stamped-or-legacy resolver the Documents Dashboard and DD unlock use.
//
// Why this matters for LMP:
// Previously we only read header.workflowConfig.obsFlow. Unstamped LMP patients
// (no workflowConfig) therefore fell through to "Observation enabled" and the
// auto-helper silently returned not-all-approved — even though the dashboard
// (via useWorkflowConfig → deriveLegacyConfig) correctly dropped the OBS row.
// resolveWorkflowConfig(header, { organisation }) mirrors that path:
//   - stamped obsFlow.observation = disabled → 4 stages (LMP)
//   - unstamped LMP org name → legacy obsFlow disabled → 4 stages
//   - TOC / absent obsFlow → Observation enabled → 5 stages (unchanged)
const getEnabledStageKeysFromHeader = (header) => {
  const wf = resolveWorkflowConfig(header, { organisation: header?.organisation });
  return getEnabledAutismStages(wf);
};

// Utility: Parse Firestore Timestamp | Date | ISO string into a JS Date, or null.
const toDateOrNull = (value) => {
  if (!value) return null;
  try {
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

// Selector for the Autism patient report processing status on the patient header.
// Passed to the shared waitForProcessingComplete so it reads the right path.
const getAutismReportStatus = (data) => data?.autismPatientReport?.prReportProcessingStatus;

// Small retry helper for eventual consistency on serverTimestamp propagation.
// Kept lightweight to avoid stressing Firestore (same shape as autoPatientReport.js).
const retry = async (fn, attempts = 3, delaysMs = [150, 400, 800]) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (i < attempts - 1) {
        await new Promise((res) => setTimeout(res, delaysMs[i] || 500));
      }
    }
  }
  throw lastError;
};

// Read the patient header + all five reportContent docs in one shot.
// Returns { header, reportContentByStage } where reportContentByStage is keyed
// by stage key (history, semiStructuredInterview, ...) → doc data (or null).
const readReportState = async (patientId) => {
  const headerRef = doc(db, ...getPatientHeaderPathSegments(patientId));
  const stageRefs = AUTISM_STAGE_KEYS.map((stageKey) =>
    doc(db, ...getPatientReportContentPathSegments(patientId, stageKey)),
  );

  const [headerSnap, ...stageSnaps] = await Promise.all([
    getDoc(headerRef),
    ...stageRefs.map((ref) => getDoc(ref)),
  ]);

  if (!headerSnap.exists()) throw new Error('patient-header-missing');

  const reportContentByStage = {};
  AUTISM_STAGE_KEYS.forEach((stageKey, i) => {
    const snap = stageSnaps[i];
    reportContentByStage[stageKey] = snap.exists() ? snap.data() : null;
  });

  return { header: headerSnap.data(), reportContentByStage };
};

// Business rule: every APPLICABLE section report must be approved before the
// patient report can be (re)finalised. Reads each stage's
// `${prefix}ReportApproved` flag. `enabledStageKeys` collapses to History + SSI
// + MDT Review + Feedback Session for orgs with Behavioural Observation disabled.
const areAllSectionsApproved = (reportContentByStage, enabledStageKeys) =>
  enabledStageKeys.every((stageKey) => {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    return reportContentByStage?.[stageKey]?.[`${prefix}ReportApproved`] === true;
  });

// The most recent section approval time across all applicable stages, or null
// if none have resolved timestamps yet.
const getSectionsMaxApprovedAt = (reportContentByStage, enabledStageKeys) => {
  const dates = enabledStageKeys.map((stageKey) => {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    return toDateOrNull(reportContentByStage?.[stageKey]?.[`${prefix}ReportApprovedAt`]);
  }).filter(Boolean);
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
};

const isReportProcessing = (header) =>
  header?.autismPatientReport?.prReportProcessingStatus === 'processing';

/**
 * Appointment-date staleness (mirrors isAppointmentDatesStale in the ADHD
 * helper, autoPatientReport.js).
 *
 * When a clinician changes a per-module appointment date on the Patient
 * Overview, `applyAppointmentDateChanges` bumps the header's top-level
 * `appointmentDatesLastChangedAt`. The Autism Patient Report embeds those dates
 * in its "Assessment Details" and per-stage appendices, so if that clock is
 * newer than `autismPatientReport.prReportApprovedAt` the finalised PDF is
 * showing an out-of-date appointment date and must be re-rendered.
 *
 * This rule NEVER bootstraps a first Patient Report: it is only consulted once
 * `prReportApprovedAt` exists (see isReportStale below). A date edit on a
 * patient whose report has not yet been finalised is a no-op here — the
 * all-sections-approved gate decides when the first render happens.
 *
 * Loop prevention holds unchanged: TriggerRenderAutismPatientReport writes
 * prReportApprovedAt at trigger time, which advances it past the
 * appointmentDatesLastChangedAt bump, so the post-render recheck sees not-stale.
 */
const isAppointmentDatesStale = (header, approvedAt) => {
  const changedAt = toDateOrNull(header?.appointmentDatesLastChangedAt);
  if (!changedAt || !approvedAt) return false;
  return changedAt.getTime() > approvedAt.getTime();
};

// The patient report is stale if it was never finalised, if any section was
// approved more recently than the last patient report finalisation, or if an
// appointment date was changed after that finalisation.
const isReportStale = (header, reportContentByStage, enabledStageKeys) => {
  const approvedAt = toDateOrNull(header?.autismPatientReport?.prReportApprovedAt);
  // First-time bootstrap: never finalised → stale.
  if (!approvedAt) return true;
  const sectionsMaxApprovedAt = getSectionsMaxApprovedAt(reportContentByStage, enabledStageKeys);
  // No resolved section timestamps yet → treat as stale to allow the first render.
  if (!sectionsMaxApprovedAt) return true;
  if (approvedAt.getTime() < sectionsMaxApprovedAt.getTime()) return true;
  return isAppointmentDatesStale(header, approvedAt);
};

// Internal: read fresh state and derive the gating booleans in one place, so the
// wait-then-retry paths re-check against live Firestore data (not stale closures).
const checkState = async (patientId) => {
  const { header, reportContentByStage } = await readReportState(patientId);
  const enabledStageKeys = getEnabledStageKeysFromHeader(header);
  return {
    header,
    reportContentByStage,
    allApproved: areAllSectionsApproved(reportContentByStage, enabledStageKeys),
    stale: isReportStale(header, reportContentByStage, enabledStageKeys),
    processing: isReportProcessing(header),
  };
};

/**
 * rerenderAutismPatientReportIfStale
 * ----------------------------------
 * Safe to call (fire-and-forget) any time a clinician exits an Autism section
 * review screen. It reads the latest Firestore state and:
 *   - no-ops fast if not all five sections are approved, or the report is already
 *     up to date;
 *   - when a render is already in-flight, waits for it to finish then triggers a
 *     single retry if the report is still stale;
 *   - otherwise triggers the render, then waits + re-checks once to catch a section
 *     that was approved while the CF was running.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} params.clinicianId
 * @returns {Promise<{success: boolean, skipped?: boolean, reason?: string, error?: string}>}
 */
export const rerenderAutismPatientReportIfStale = async ({ patientId, clinicianId }) => {
  if (!patientId || !clinicianId) {
    return { success: false, skipped: true, reason: 'missing-params' };
  }

  // 12-min timeout for the wait: the render CF's client timeout is 6 min, and the
  // backend reconcile sweep runs at 12 min — this must comfortably exceed the CF so
  // we don't give up before it finishes and miss the post-completion retry.
  const WAIT_TIMEOUT_MS = 12 * 60 * 1000;
  const waitForRender = () =>
    waitForProcessingComplete(patientId, 'prReportProcessingStatus', WAIT_TIMEOUT_MS, getAutismReportStatus);

  try {
    // Read fresh (with short retry) to avoid racing the serverTimestamp writes from
    // the section approval transaction that just committed.
    const { header, reportContentByStage } = await retry(() => readReportState(patientId), 3);
    const enabledStageKeys = getEnabledStageKeysFromHeader(header);

    // Gate: every applicable section report must be approved.
    if (!areAllSectionsApproved(reportContentByStage, enabledStageKeys)) {
      return { success: true, skipped: true, reason: 'not-all-approved' };
    }
    // Gate: skip if the patient report is already up to date.
    if (!isReportStale(header, reportContentByStage, enabledStageKeys)) {
      return { success: true, skipped: true, reason: 'not-stale' };
    }

    // If another caller is already rendering, wait for it, then re-check and trigger
    // one retry if the completed render didn't capture our latest approval.
    if (isReportProcessing(header)) {
      console.log('Autism patient report already processing — waiting before re-checking staleness');
      await waitForRender();

      const recheck = await checkState(patientId);
      if (!recheck.stale || recheck.processing || !recheck.allApproved) {
        return { success: true, skipped: true, reason: 'resolved-after-wait' };
      }

      console.log('Autism patient report still stale after in-flight render — triggering retry');
      return await TriggerRenderAutismPatientReport({ patientId, clinicianId });
    }

    // Happy path: not currently processing and report is stale — trigger the render.
    const result = await TriggerRenderAutismPatientReport({ patientId, clinicianId });

    // After the render completes, re-check once: another section may have been
    // approved while the CF was running. Trigger exactly one retry if still stale.
    try {
      await waitForRender();
      const recheck = await checkState(patientId);
      if (recheck.stale && !recheck.processing && recheck.allApproved) {
        console.log('Autism patient report stale after render — triggering one retry');
        await TriggerRenderAutismPatientReport({ patientId, clinicianId });
      }
    } catch (retryErr) {
      console.error('Post-render staleness recheck failed:', retryErr);
    }

    return result;
  } catch (error) {
    console.error('rerenderAutismPatientReportIfStale error:', error);
    return { success: false, error: error.message || 'unknown' };
  }
};

export const __testOnly = {
  toDateOrNull,
  readReportState,
  areAllSectionsApproved,
  getSectionsMaxApprovedAt,
  isReportProcessing,
  isAppointmentDatesStale,
  isReportStale,
  checkState,
  getEnabledStageKeysFromHeader,
};
